// CommentOverlay.jsx - Simplified version without complex Framer Motion integration
import {
  Modal,
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  Avatar,
  Divider,
  Popover,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from "@mui/material";
import { AiOutlineClose, AiOutlineSmile, AiOutlineDelete } from "react-icons/ai";
import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import { useSelector } from "react-redux";

// Add emoji options - you can expand this list as needed
const emojiOptions = [
  "😀", "😂", "🙂", "😍", "😎", 
  "👍", "❤️", "🔥", "✨", "🎉",
  "👏", "🤔", "😮", "🙌", "💯",
  "🤣", "😊", "🥰", "😇", "🤩",
  "💪", "🙏", "👌", "🤷‍♀️", "🤷‍♂️"
];

// Update the CommentOverlay component to handle user identification, emojis, and comment deletion
const CommentOverlay = ({ open, onClose, postId, comments = [], addComment, deleteComment }) => {
  const [newComment, setNewComment] = useState("");
  const [localComments, setLocalComments] = useState([]);
  const user = useSelector((state) => state.user); // Get current user from Redux
  const currentUserId = user?._id;
  
  // Emoji picker state
  const [anchorEl, setAnchorEl] = useState(null);
  const showEmojiPicker = Boolean(anchorEl);
  
  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState(null);

  // Update the handleDeleteComment function to show the dialog
  const handleDeleteComment = (commentId) => {
    setCommentToDelete(commentId);
    setDeleteDialogOpen(true);
  };
  
  // Add this new function to handle the confirmed deletion
  const confirmDeleteComment = () => {
    if (commentToDelete && typeof deleteComment === 'function') {
      deleteComment(postId, commentToDelete);
      setDeleteDialogOpen(false);
      setCommentToDelete(null);
    }
  };
  
  // Add this function to handle cancellation
  const cancelDeleteComment = () => {
    setDeleteDialogOpen(false);
    setCommentToDelete(null);
  };
  
// Update the useEffect that handles comments in CommentOverlay.jsx
useEffect(() => {
  if (open && postId) {
    if (comments && comments.length > 0) {
      const processedComments = comments
        .map(comment => ({
          ...comment,
          isCurrentUser: comment.userId === currentUserId
        }))
        .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp)); // oldest first

      setLocalComments(processedComments);
    } else {
      setLocalComments([]);
    }
  }
}, [open, postId, comments, currentUserId]);

  
  const handlePost = () => {
    if (newComment.trim() && postId) {
      // Call parent's addComment if it exists
      if (typeof addComment === 'function') {
        // Send the new comment text to parent component
        addComment(postId, newComment.trim());
        setNewComment("");
      }
    }
  };
  
  // Handle emoji click
  const handleEmojiClick = (emoji) => {
    setNewComment(prev => prev + emoji);
    setAnchorEl(null); // Close the emoji picker
  };
  
  // Toggle emoji picker
  const handleEmojiButtonClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  
  // Close emoji picker
  const handleCloseEmojiPicker = () => {
    setAnchorEl(null);
  };
  
  return (
    <>
      <Modal open={open} onClose={onClose}>
        <Box
          sx={{
            position: "absolute",
            bottom: { xs: "20%", sm: "15%", md: "15%" },
            left: { xs: "50%", sm: "50%", md: "55%" },
            transform: "translateX(-50%)",
            width: { xs: "90%", sm: "90%", md: "95%" },
            maxWidth: { xs: 350, sm: 380, md: 420 },
            height: { xs: "65vh", sm: "68vh", md: "70vh" },
            bgcolor: "#fff",
            borderRadius: { xs: "16px", md: "16px 16px 0 0" },
            display: "flex",
            flexDirection: "column",
            boxShadow: "0 -2px 10px rgba(0,0,0,0.1)",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              p: { xs: 1.5, sm: 2 },
              borderBottom: "1px solid #eee",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 600,
                fontSize: { xs: "1.1rem", sm: "1.25rem" }
              }}
            >
              Comments
            </Typography>
            <IconButton onClick={onClose}>
              <AiOutlineClose />
            </IconButton>
          </Box>

          {/* Comments Section (Scrollable) */}
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              px: { xs: 1.5, sm: 2 },
              py: 1,
            }}
          >
            {localComments.length === 0 ? (
              <Typography sx={{ color: "#aaa", textAlign: "center", mt: 5 }}>
                No comments yet. Be the first one!
              </Typography>
            ) : (
              localComments.map((comment, i) => (
                <Box key={i} sx={{ 
                  display: "flex", 
                  gap: { xs: 1.5, sm: 2 }, 
                  mb: 2,
                  backgroundColor: comment.isCurrentUser ? "rgba(33, 150, 243, 0.05)" : "transparent",
                  padding: { xs: 1, sm: 1.5 },
                  borderRadius: 2,
                  position: "relative" // Added for delete button positioning
                }}>
                  <Avatar
                    src={comment.profilePic || `https://i.pravatar.cc/150?img=${i + 10}`}
                    alt={comment.user || "User"}
                    sx={{ 
                      width: { xs: 32, sm: 36 }, 
                      height: { xs: 32, sm: 36 } 
                    }}
                  />
                  <Box sx={{ flex: 1, overflow: "hidden" }}>
                    <Typography sx={{ 
                      fontWeight: 600, 
                      fontSize: { xs: "0.85rem", sm: "0.9rem" },
                      color: comment.isCurrentUser ? "#2196f3" : "inherit"
                    }}>
                      {comment.isCurrentUser ? "You" : comment.user || "Anonymous"}
                    </Typography>
                    <Typography
                      sx={{ 
                        color: "#555", 
                        fontSize: { xs: "0.8rem", sm: "0.85rem" }, 
                        mt: 0.5,
                        wordBreak: "break-word" // Ensures text doesn't overflow
                      }}
                    >
                      {typeof comment.text === 'string' ? comment.text : ''}
                    </Typography>
                    <Typography
                      sx={{ 
                        fontSize: { xs: "0.65rem", sm: "0.7rem" }, 
                        color: "#999", 
                        mt: 0.3 
                      }}
                    >
                      {comment.timestamp 
                        ? new Date(comment.timestamp).toLocaleDateString([], {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          }) + " • " + new Date(comment.timestamp).toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                          })
                        : ""}
                    </Typography>
                  </Box>
                  
                  {/* Delete button for user's own comments */}
                  {comment.isCurrentUser && (
                    <IconButton 
                      size="small" 
                      color="error"
                      onClick={() => handleDeleteComment(comment._id || comment.commentId)}
                      sx={{ 
                        position: "absolute",
                        right: 8,
                        top: 8,
                        opacity: 0.7,
                        '&:hover': {
                          opacity: 1
                        }
                      }}
                    >
                      <AiOutlineDelete size={18} />
                    </IconButton>
                  )}
                </Box>
              ))
            )}
          </Box>

          <Divider />

          {/* Input */}
          <Box
            sx={{
              p: { xs: 1.5, sm: 2 },
              display: "flex",
              gap: 1,
              borderTop: "1px solid #eee",
              bgcolor: "#fafafa",
            }}
          >
            <Box sx={{ position: "relative", width: "100%" }}>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                placeholder="Add a comment..."
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === "Enter") handlePost();
                }}
                sx={{
                  backgroundColor: "white",
                  borderRadius: 2,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    "& fieldset": {
                      borderColor: "#ccc",
                    },
                    "&:hover fieldset": {
                      borderColor: "#bbb",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#555",
                      boxShadow: "none",
                    },
                    paddingRight: "40px", // Make room for emoji button
                  },
                }}
              />
              <IconButton 
                onClick={handleEmojiButtonClick}
                sx={{ 
                  position: "absolute", 
                  right: 8, 
                  top: "50%", 
                  transform: "translateY(-50%)",
                  color: "#888"
                }}
              >
                <AiOutlineSmile />
              </IconButton>
              
              {/* Emoji Picker Popover */}
              <Popover
                open={showEmojiPicker}
                anchorEl={anchorEl}
                onClose={handleCloseEmojiPicker}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
              >
                <Box sx={{ 
                  p: 1, 
                  width: { xs: 200, sm: 240 },
                  maxHeight: 230, 
                  display: "flex", 
                  flexWrap: "wrap", 
                  gap: 0.5,
                  bgcolor: "#fff",
                  borderRadius: 1,
                  boxShadow: "0 2px 10px rgba(0,0,0,0.1)"
                }}>
                  {emojiOptions.map((emoji, index) => (
                    <Button
                      key={index}
                      onClick={() => handleEmojiClick(emoji)}
                      sx={{ 
                        minWidth: "auto", 
                        fontSize: { xs: "1.1rem", sm: "1.2rem" },
                        p: 0.5
                      }}
                    >
                      {emoji}
                    </Button>
                  ))}
                </Box>
              </Popover>
            </Box>

            <Button
              onClick={handlePost}
              variant="contained"
              sx={{
                color: "#fff",
                background: "linear-gradient(135deg, #F02C56 0%, #FF6A88 100%)",
                boxShadow: "none",
                textTransform: "none",
                borderRadius: 2,
                px: { xs: 2, sm: 3 },
                minWidth: { xs: "60px", sm: "auto" },
                "&:hover": {
                  background: "linear-gradient(135deg, #e81e4d 0%, #ff4d6d 100%)",
                  boxShadow: "none",
                },
              }}
            >
              Post
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Separate Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={cancelDeleteComment}
      >
        <DialogTitle 
          sx={{ 
            background: "linear-gradient(135deg, #F02C56 0%, #FF6A88 100%)",
            color: "white",
            py: 1.5
          }}
        >
          Delete Comment
        </DialogTitle>
        <DialogContent sx={{ mt: 2, pt: 1 }}>
          <DialogContentText>
            Are you sure you want to delete this comment? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={cancelDeleteComment} 
            sx={{ 
              color: "#555",
              "&:hover": { backgroundColor: "#f5f5f5" }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={confirmDeleteComment}
            variant="contained" 
            sx={{
              bgcolor: "#f44336",
              color: "white",
              "&:hover": { bgcolor: "#d32f2f" },
              textTransform: "none",
              boxShadow: "none"
            }}
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CommentOverlay;